import React, { useEffect } from 'react'
import TopBar from '../components/TopBar'
import Container from '@mui/material/Container'
import { BottomProgress } from '../components/progress/BottomProgress'
import { NavFooter } from '../components/NavFooter'
import { useApp } from '../context/useApp'
import { AUTO_ENTRY } from '../parameters/parameters'
import Hourglass from '../components/HourGlass'
import _ from 'lodash'
import { FSM_STATES } from '../context/defaultState'
import { NaryAssignation } from './naryAssignation/NaryAssignation'
import { RankForRound } from './rankForRound/RankForRound'
import { Thanks } from '../screens/Thanks'
import { MyDialog } from '../components/MyDialog'
import { getFlags } from '../components/progress/useProgress'
import { downloadAny } from '../utils/save'
import type { TypographyVariant } from '../types/mui'
import { Branding } from '../screens/Branding'
import { HowTo } from '../screens/HowTo'
import { HowToBuckets } from '../screens/HowToBuckets'
import { AwardRound } from '../screens/AwardRound'
import SignIn from '../screens/SignIn'
import { Intro } from '../screens/Intro'
import { useAlert } from '../hooks/useAlert'
import { RankForBucket } from './rankForBucket/RankForBucket'
import { CommonContext } from '../context/CommonContext'
import { RANKING } from '../context/types'
import { apiService } from '../services/apiServices'
import { info, logException } from '../services/sentryLogger'
import { isApiDisabled } from '../services/envService'
import { Error } from '../screens/Error'
import { AssessmentDTO, AssignedBuckets } from '../services/types'
import { VALUES } from '../utils/values/cats'
import { isArabic, langIndex2AppLang } from '../i18n/useTranslation'
import { ScalePicker } from './360/ScalePicker'
import { Thanks360 } from '../screens/Thanks360'
import { downloadSession, resetSession } from '../context/session/helpers'
import { diffSeconds } from '../utils'
import { useAutoLogin } from '../hooks/useAutoLogin'
import { NPS360 } from '../screens/NPS360'
import { HowToWorkEnv } from '../screens/HowToWorkEnv'
import { HowTo360 } from '../screens/HowTo360'
import { HowToWorkNPS } from '../screens/HowToNPS'
import { Ranker } from './qsortV2/ranker'
import { HowToQSort } from './qsortV2/HowToQSort'
import { Box, useTheme } from '@mui/material'

const DEFAULT_TITLE = 'h4' as TypographyVariant
const DEFAULT_BODY = 'h6' as TypographyVariant

export const App = () => {
    const muiTheme = useTheme()
    const { alertState, alertProps } = useAlert()
    const commons = useApp()
    const { isAuto, queryParams } = useAutoLogin(commons)
    let {
        asgnsForRound,
        rounds,
        progress,
        progresses,
        remainingSteps,
        toggleDrawer,
        toggled,
        state,
        scannedItems,
        allStms,
        ...restCommons
    } = commons
    let { fsmState, params, currentRound, currentStmIndex, startedAt } = state
    const { assessmentParams, brandParams } = params

    useEffect(() => {
        document.title = `FITME assessement [${
            process.env.REACT_APP_BUILD_VERSION ?? 'LOCAL'
        }]`
    }, [])

    const submitAssessment = async (
        assessment: AssignedBuckets
    ): Promise<boolean> => {
        try {
            /*
                WILL NOT THROW FOR KNOW ERRORS!
                {
                    "code": "ER_DUP_ENTRY",
                    "status": 400,
                    "message": "Internal Error",
                    "name": "AxiosError"
                } 
            */
            const [code, subcode] = await apiService.publishAssessment({
                ...assessment,
                slug: commons.slug,
                source: commons.source,
                from: commons.from,
                startDate: startedAt.toISOString(),
                endDate: new Date().toISOString(),
                durationSeconds:
                    commons.sessionData.restorable.durationAtSave +
                    diffSeconds(
                        new Date(commons.sessionData.restorable.savedDateStr)
                    ),
            } as AssessmentDTO,
            assessmentParams.isQSortV2
        )
            info('publishAssessment', {
                code,
                subcode,
            })

            if (code === 'OK') {
                resetSession()
                restCommons.setState((s) => ({
                    ...s,
                    fsmState: FSM_STATES.FINAL_OVERVIEW,
                    // For the V2
                    currentBucketRanking: s.currentBucketRanking + 1,
                    overrideTitle: '',
                    overrideSubTitle: '',
                    error: null,
                }))
                return true
            } else {
                console.warn('publishAssessment', code, subcode)
                if (assessmentParams['debug_mode'] !== 1) {
                    alert(
                        '[debug] Error ' +
                            code + // i.e. 'BAD_REQUEST'
                            ' ' +
                            subcode // i.e. 'ER_DUP_ENTRY'
                    )
                }
                restCommons.setState((s) => ({
                    ...s,
                    fsmState: FSM_STATES.ERROR,
                    error: [code, subcode],
                }))
                return false
            }
        } catch (e: any) {
            logException('publishAssessment', 'Unknown error code', e)
            return false
        }
    }

    const target360Stm =
        allStms?.[
            fsmState === FSM_STATES.PICKER_LEADERSHIP_360
                ? 'leadership'
                : 'workenv'
        ][currentStmIndex]
    const langIndex = assessmentParams['langIndex']
    const { isStateWSortableList } = getFlags(fsmState)
    info('============== rerender App CORE', fsmState, { params, isAuto })

    if (fsmState === FSM_STATES['AUTH'] && isAuto) {
        // blank FIXME
        return <div />
    }
    return (
        <Box
            // className="h-full"
            // className="h-full flex flex-col"
            // className="min-h-screen flex flex-col"
            // DOC : min-h-screen is required to avoid black background from index.html and fitme.css when the content is overflowing
            className="min-h-[100dvh] flex flex-col"
            style={{
                background: `linear-gradient(to bottom, ${muiTheme.palette.primary.dark}, ${muiTheme.palette.primary.light})`,
            }}
        >
            <CommonContext.Provider
                value={{
                    ...commons,
                    ...alertState,
                }}
            >
                <meta
                    name="theme-color"
                    content={muiTheme.palette.primary.main}
                />
                <Container
                    sx={{
                        direction: isArabic(params) ? 'rtl' : 'ltr',
                    }}
                    maxWidth="lg"
                    disableGutters
                    fixed
                    // className="h-full flex
                    // flex-1 flex-col
                    // select-none"
                    className="flex flex-1 flex-col select-none"
                >
                    <MyDialog {...alertProps} />

                    {restCommons.hasTopTitle && !assessmentParams.isQSortV2 && (
                        <TopBar
                            title={commons.overrideTitle || commons.topTitle}
                            subtitle={
                                commons.overrideSubTitle || commons.topSubtitle
                            }
                            topLeft={restCommons.topLeft}
                            topRight={restCommons.topRight}
                            topCenter={restCommons.topCenter}
                        />
                    )}
                    {fsmState === FSM_STATES['AUTH'] && !isAuto && (
                        <SignIn {...queryParams} />
                    )}

                    {fsmState !== FSM_STATES['AUTH'] &&
                        fsmState !== FSM_STATES['NONE'] && (
                            <Container
                                className="flex flex-1 flex-col justify-between pt-2"
                                style={{
                                    backgroundColor:
                                        muiTheme.palette.background.default,
                                }}
                            >
                                {fsmState === FSM_STATES['INTRO'] && (
                                    <Intro
                                        titleV={DEFAULT_TITLE}
                                        bodyV={DEFAULT_BODY}
                                    />
                                )}

                                {fsmState === FSM_STATES['HOW_TO_BINARY'] && (
                                    <HowTo
                                        titleV={DEFAULT_TITLE}
                                        bodyV={DEFAULT_BODY}
                                    />
                                )}

                                {fsmState === FSM_STATES['BRANDING'] && (
                                    <Branding
                                        titleV={DEFAULT_TITLE}
                                        bodyV={DEFAULT_BODY}
                                    />
                                )}

                                {fsmState === FSM_STATES['RANK_14_X_BY_X'] &&
                                    assessmentParams['Stm / comparison'] ===
                                        AUTO_ENTRY.v && (
                                        <NaryAssignation commons={commons} />
                                    )}

                                {fsmState === FSM_STATES['AWARD_ROUND'] && (
                                    <AwardRound
                                        hasConfirmRound={
                                            assessmentParams.hasConfirmRound
                                        }
                                        currentRound={currentRound}
                                        titleV={DEFAULT_TITLE}
                                        bodyV={DEFAULT_BODY}
                                    />
                                )}

                                {isStateWSortableList && <RankForRound />}

                                {fsmState === FSM_STATES['HOW_TO_BUCKETS'] && (
                                    <HowToBuckets
                                        titleV={DEFAULT_TITLE}
                                        bodyV={'h5'}
                                    />
                                )}

                                {fsmState === FSM_STATES['RANK_FOR_BUCKET'] && (
                                    <RankForBucket commons={commons} />
                                )}

                                {fsmState === FSM_STATES['FINAL_OVERVIEW'] && (
                                    <Thanks
                                        disabled={
                                            assessmentParams[
                                                'download_report'
                                            ] === 0
                                        }
                                        onQuit={() =>
                                            apiService.logout(
                                                brandParams.returnUrl
                                            )
                                        }
                                        onClick={() => {
                                            // if (
                                            //     assessmentParams[
                                            //         'debug_mode'
                                            //     ] === 1
                                            // ) {
                                            //     try {
                                            //         downloadSession(
                                            //             commons.sessionData
                                            //         )
                                            //     } catch (e: any) {
                                            //         //FIXME
                                            //         logException(
                                            //             'FINAL_OVERVIEW',
                                            //             'downloadSession',
                                            //             e
                                            //         )
                                            //     }
                                            // }

                                            !isApiDisabled() &&
                                                apiService
                                                    .downloadReport(
                                                        langIndex2AppLang(
                                                            langIndex
                                                        )
                                                    )
                                                    .then((reportBlob) => {
                                                        const url =
                                                            window.URL.createObjectURL(
                                                                reportBlob
                                                            )
                                                        // FIXME clean
                                                        // window.myUrl = url
                                                        downloadAny(
                                                            'report.pdf',
                                                            url
                                                        )
                                                    })
                                                    .catch((e) =>
                                                        logException(
                                                            'report',
                                                            'Unknown',
                                                            e
                                                        )
                                                    )
                                        }}
                                    />
                                )}

                                {fsmState === FSM_STATES.HOW_TO_360 && (
                                    <HowTo360
                                        titleV={DEFAULT_TITLE}
                                        bodyV={DEFAULT_BODY}
                                    />
                                )}
                                {[
                                    FSM_STATES.PICKER_360,
                                    FSM_STATES.PICKER_WORKENV_360,
                                    FSM_STATES.PICKER_LEADERSHIP_360,
                                ].includes(fsmState) &&
                                    target360Stm && (
                                        <ScalePicker
                                            fsmState={fsmState}
                                            stm={target360Stm}
                                        />
                                    )}

                                {fsmState === FSM_STATES.HOW_TO_WORKENV && (
                                    <HowToWorkEnv
                                        titleV={DEFAULT_TITLE}
                                        bodyV={DEFAULT_BODY}
                                    />
                                )}

                                {fsmState === FSM_STATES.HOW_TO_NPS && (
                                    <HowToWorkNPS
                                        titleV={DEFAULT_TITLE}
                                        bodyV={DEFAULT_BODY}
                                    />
                                )}

                                {fsmState === FSM_STATES.NPS_360 && <NPS360 />}

                                {fsmState === FSM_STATES.THANKS_360 && (
                                    <Thanks360
                                        onQuit={() =>
                                            apiService.logout(
                                                brandParams.returnUrl
                                            )
                                        }
                                    />
                                )}

                                {fsmState === FSM_STATES['ERROR'] && (
                                    <Error
                                        disabled={
                                            assessmentParams[
                                                'download_report'
                                            ] === 0
                                        }
                                        error={
                                            state?.error ?? ['UNKNOWN', 'ERROR']
                                        }
                                        onClick={() => {
                                            if (
                                                assessmentParams[
                                                    'debug_mode'
                                                ] === 1
                                            ) {
                                                try {
                                                    downloadSession(
                                                        commons.sessionData
                                                    )
                                                } catch (e: any) {
                                                    logException(
                                                        'FINAL_OVERVIEW',
                                                        'downloadSession',
                                                        e
                                                    )
                                                }
                                            }

                                            info('Error', 'clicked')
                                            if (
                                                state?.error?.[0] ===
                                                    'BAD_REQUEST' &&
                                                state?.error?.[1] ===
                                                    'ER_DUP_ENTRY'
                                            ) {
                                                !isApiDisabled() &&
                                                    apiService
                                                        .downloadReport(
                                                            langIndex2AppLang(
                                                                langIndex
                                                            )
                                                        )
                                                        .then((blob) =>
                                                            downloadAny(
                                                                'report.pdf',
                                                                window.URL.createObjectURL(
                                                                    blob
                                                                )
                                                            )
                                                        )
                                                        .catch((e) =>
                                                            logException(
                                                                'report',
                                                                'Unknown',
                                                                e
                                                            )
                                                        )
                                            } else if (
                                                state?.error?.[1] ===
                                                'VALIDATION'
                                            ) {
                                                // FIXME not the best
                                                // FIXME duplicated code with useFSM()
                                                const ranking =
                                                    commons?.sessionData
                                                        ?.ranking ?? []
                                                const assessment =
                                                    ranking.reduce<AssignedBuckets>(
                                                        (acc, current) => {
                                                            const value =
                                                                current.valueKey as VALUES
                                                            acc[value] =
                                                                (current.bucket as RANKING) ??
                                                                1 // FIXME
                                                            return acc
                                                        },
                                                        {} as AssignedBuckets
                                                    )
                                                submitAssessment(assessment)
                                            }
                                        }}
                                    />
                                )}

                                {fsmState === FSM_STATES['HOW_TO_Q_SORT'] && (
                                    <HowToQSort
                                        titleV={DEFAULT_TITLE}
                                        bodyV={DEFAULT_BODY}
                                    />
                                )}

                                {fsmState === FSM_STATES.QSORT_V2_RANKER && (
                                    <Ranker
                                        onSubmit={(asgn: AssignedBuckets) =>
                                            submitAssessment(asgn)
                                        }
                                    />
                                )}

                                <div>
                                    {!assessmentParams.isQSortV2 &&
                                        assessmentParams?.['Hourglass'] ===
                                            1 && (
                                            <Hourglass
                                                duration={
                                                    assessmentParams?.[
                                                        'Hourglass'
                                                    ]
                                                }
                                                onComplete={
                                                    commons?.bottomRight
                                                        ?.onClick ??
                                                    console.error
                                                }
                                            />
                                        )}

                                    {restCommons.hasProgress &&
                                        !assessmentParams.isQSortV2 && (
                                            <BottomProgress
                                                currentStmIndex={
                                                    currentStmIndex
                                                }
                                                currentRound={currentRound}
                                                params={params}
                                                isRunning={
                                                    !!restCommons.isRunning
                                                }
                                                progress={
                                                    restCommons.isRunning
                                                        ? progress
                                                        : 0
                                                }
                                                simple_progress_bar={
                                                    assessmentParams?.[
                                                        'simple_progress_bar'
                                                    ]
                                                }
                                                progresses={
                                                    restCommons.isRunning
                                                        ? progresses
                                                        : [0, 0, 0, 0]
                                                }
                                                remainingTime={
                                                    restCommons.remainingTime
                                                }
                                                remainingSteps={
                                                    assessmentParams?.[
                                                        'Remaining steps'
                                                    ]
                                                        ? remainingSteps
                                                        : undefined
                                                }
                                                startedAt={startedAt}
                                                currentStms={asgnsForRound}
                                            />
                                        )}

                                    {
                                        // !assessmentParams.isQSortV2 &&
                                        restCommons.hasBottomNav && (
                                            <NavFooter
                                                bottomLeft={
                                                    restCommons.bottomLeft
                                                }
                                                bottomRight={
                                                    restCommons.bottomRight
                                                }
                                                bottomCenter={
                                                    restCommons.bottomCenter
                                                }
                                                assignations={asgnsForRound}
                                                onToggle={toggleDrawer(
                                                    'bottom'
                                                )}
                                                toggled={toggled('bottom')}
                                            />
                                        )
                                    }
                                </div>
                            </Container>
                        )}
                </Container>
            </CommonContext.Provider>
        </Box>
    )
}
